/**
 * DebugLog — Logging system with categorized messages.
 * Captures errors, stores all logs, and exposes via window.__debug.log.
 */

class DebugLog {
  constructor() {
    this._logs = [];
    this._maxLogs = 1000;
    this._initialized = false;
  }

  /**
   * Initialize the debug log. Captures global errors.
   */
  init() {
    if (this._initialized) return;
    this._initialized = true;

    // Capture unhandled errors
    window.onerror = (message, source, lineno, colno, error) => {
      this.error(`[Uncaught] ${message} at ${source}:${lineno}:${colno}`);
      return false; // Let the default handler run too
    };

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      const msg = reason instanceof Error ? reason.message : String(reason);
      this.error(`[UnhandledRejection] ${msg}`);
    });

    this.info('DebugLog initialized');
  }

  /**
   * Internal log method.
   * @param {string} level
   * @param {string} msg
   */
  _log(level, msg) {
    const entry = {
      time: Date.now(),
      level,
      msg,
    };
    this._logs.push(entry);

    // Cap log size
    if (this._logs.length > this._maxLogs) {
      this._logs.shift();
    }

    // Also output to browser console
    const prefix = `[${level.toUpperCase()}]`;
    switch (level) {
      case 'error':
        console.error(prefix, msg);
        break;
      case 'warn':
        console.warn(prefix, msg);
        break;
      case 'event':
        console.log(`%c${prefix} ${msg}`, 'color: #4fc3f7');
        break;
      default:
        console.log(prefix, msg);
    }
  }

  info(msg) {
    this._log('info', msg);
  }

  warn(msg) {
    this._log('warn', msg);
  }

  error(msg) {
    this._log('error', msg);
  }

  event(msg) {
    this._log('event', msg);
  }

  /**
   * Get all stored log entries.
   * @returns {Array}
   */
  getLogs() {
    return this._logs;
  }

  /**
   * Clear all stored logs.
   */
  clear() {
    this._logs = [];
  }

  /**
   * Placeholder for uploading logs to a server.
   */
  async upload() {
    this.info('Log upload: not yet implemented');
    // Future: POST to /api/logs with this._logs
  }
}

const debugLog = new DebugLog();
export default debugLog;
